import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import React, { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Toaster } from 'react-hot-toast';
import { Route, Routes } from 'react-router-dom';
import { ErrorFallbackPrincipals } from './components/ErrorBoundary/ErrorFallbackPrincipals';
import { Loading } from './components/Loading';
import { ContentController } from './controllers/ContentController';
import CookieStatement from './features/CookieStatement/CookieStatement';
import { Intro } from './features/Intro';
import TermsOfUse from './features/TermsOfUse/TermsOfUse';
import { tracking } from './lib/tracking';
import { shortenEnvironmentName } from './lib/utils';
import './tailwind.css';
import { usePrincipals } from './hooks/queries/usePrincipals';

const App: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const { data: principals } = usePrincipals();

  useEffect(() => {
    instance.enableAccountStorageEvents();
    return () => {
      instance.disableAccountStorageEvents();
    };
  });

  useEffect(() => {
    const environment = shortenEnvironmentName(
      process.env.REACT_APP_ENVIRONMENT,
    );
    tracking.push('site', 'Init', {
      environment,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      language: 'en',
    });
  }, []);

  return (
    <>
      {isAuthenticated ? (
        principals ? (
          // logged in to MS Active Directory and firebase auth, no error occured
          // error boundary if no prinipal is selected (not in url and localStorage)
          <ErrorBoundary
            FallbackComponent={({ error, resetErrorBoundary }) => (
              <ErrorFallbackPrincipals
                userId="Principal will not be persisted on login."
                error={error}
                resetErrorBoundary={resetErrorBoundary}
              />
            )}
          >
            <ContentController />
          </ErrorBoundary>
        ) : (
          <Loading
            className="flex w-full items-center justify-center"
            loading
          />
        )
      ) : inProgress !== 'none' ? (
        // MS Active Directory authentication is running
        <Loading className="flex w-full items-center justify-center" loading />
      ) : (
        // not logged in to both MS Active Directory (also occurs when not completely logged out (sign-back-in via MS AD)) + firebase Auth or authError occured
        <Routes>
          <Route path="/cookie-statement" element={<CookieStatement />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          {/* routing to intro-component on all subpages */}
          <Route path="/*" element={<Intro />} />
        </Routes>
      )}
      <Toaster
        position="bottom-center"
        containerStyle={{
          top: 0,
          left: 0,
          bottom: 72,
          right: 0,
        }}
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
        }}
      />
    </>
  );
};

export default App;
